import { useQuery } from "@apollo/client/react";
import workBlockContextMenu from "components/common/Scheduler/contextMenu/workBlockContextMenu";
import NewBlockTooltip from "components/common/Scheduler/SchedulerRenderers/components/blocks/NewBlockTooltip";
import { blockRenderer, blockTooltipRenderer } from "components/common/Scheduler/SchedulerRenderers/renderers";
import packmanagerJobLinkRenderer from "components/common/Scheduler/SchedulerRenderers/renderers/packmanagerJobLinkRenderer";
import renderToString from "components/common/Scheduler/SchedulerRenderers/renderers/renderToString";
import OverlappingPlannedBreaks from "components/pages/schedule/helpers/OverlappingPlannedBreaks";
import ConfirmModalContext from "contexts/ConfirmModalContext";
import Block from "domain/Block";
import WorkBlock from "domain/WorkBlock";
import useApplicationContext from "hooks/apollo/applicationContext/useApplicationContext";
import useRescheduleBlocks from "hooks/apollo/blocks/useRescheduleBlocks";
import useUnscheduleBlock from "hooks/apollo/blocks/useUnscheduleBlock";
import useGetLines from "hooks/apollo/line/useGetLines";
import { GetTeardownTimes } from "hooks/apollo/teardownTime/teardownTime.gql";
import useTrackUsage from "hooks/apollo/usageData/useTrackUsage";
import useFullyScheduleWorkOrder from "hooks/apollo/workBlock/useFullyScheduleWorkOrder";
import useSplitWorkBlockAcrossShifts from "hooks/apollo/workBlock/useSplitWorkBlockAcrossShifts";
import useUnscheduleWorkOrder from "hooks/apollo/workOrder/useUnscheduleWorkOrder";
import useUpdateWorkOrder from "hooks/apollo/workOrder/useUpdateWorkOrder";
import { useDowntimeShowModal, useWorkBlockEditModal, useWorkBlockShowModal } from "hooks/state";
import useNotesPopUp from "hooks/state/useNotesPopUp";
import usePublishWorkBlockModal from "hooks/state/usePublishWorkBlockModal";
import useSlideOutState from "hooks/state/useSlideOutState";
import assign from "lodash/assign";
import Authorization from "policies/Authorization";
import { useContext } from "react";
import DateTime from "utils/DateTime";
import Timespan from "utils/Timespan";

function workBlockReadOnlyMenu({ openWorkBlockShowModal, blockData }) {
  return {
    view: {
      onItem: () => openWorkBlockShowModal(blockData),
      text: "View details",
    },
  };
}

function downtimeReadOnlyMenu({ openDowntimeShowModal, blockData }) {
  return {
    view: {
      onItem: () => openDowntimeShowModal(blockData),
      text: "View details",
    },
  };
}

function downtimeMenu({ items, unscheduleBlock }) {
  return {
    deleteEvent: {
      icon: false,
      name: "unschedule-downtime",
      onItem: unscheduleBlock,
      text: "Delete downtime block",
    },
    editEvent: {
      ...items.editEvent,
      icon: false,
      text: "Edit downtime block",
    },
  };
}
export default function useEventFeatures() {
  const policy = Authorization.usePolicy();
  const {
    data: { teardownTimes },
  } = useQuery(GetTeardownTimes);

  const { lines } = useGetLines({ activeOnly: true });

  const publishWorkBlockModal = usePublishWorkBlockModal();
  const splitWorkBlockAcrossShifts = useSplitWorkBlockAcrossShifts();

  const { openWorkOrderDetails } = useSlideOutState();
  const handleUpdateWorkOrder = useUpdateWorkOrder();
  const handleUnscheduleWorkOrder = useUnscheduleWorkOrder();
  const handleUnscheduleBlock = useUnscheduleBlock();
  const { confirmAction } = useContext(ConfirmModalContext);

  const onTrackUsage = useTrackUsage();
  const rescheduleBlocks = useRescheduleBlocks();
  const { open: onWorkBlockShowModal } = useWorkBlockShowModal();
  const { open: onDowntimeShowModal } = useDowntimeShowModal();
  const { open: onOpenEditWorkBlock } = useWorkBlockEditModal();
  const applicationContext = useApplicationContext();
  const canUpdate = policy.canUpdate(Authorization.EntityTypes.WorkBlock);
  const fullyScheduleWorkOrder = useFullyScheduleWorkOrder();
  const handleUnscheduleTeardownTime = ({ eventRecord: { data: blockData } }) => {
    const { block: workBlock } = blockData;

    const updatedWorkBlock = WorkBlock.updateBlock(workBlock, {
      teardownTimeDuration: 0,
      teardownTimeLabel: "Teardown Time",
    });

    rescheduleBlocks([updatedWorkBlock]);
  };

  const handlePublishWorkBlock = ({ eventRecord: { data: blockData } }) => {
    const { block: workBlock } = blockData;

    publishWorkBlockModal.open(workBlock);
  };

  const handleSplitWorkBlockAcrossShifts = ({ eventRecord: { data: blockData } }) => {
    const { block: workBlock } = blockData;

    splitWorkBlockAcrossShifts(workBlock);
  };

  const onScheduleTeardownTime = (updatedBlock) => {
    onTrackUsage("schedule_teardown_time");
    rescheduleBlocks([updatedBlock]);
  };
  const openWorkBlockShowModal = (blockData) => {
    onWorkBlockShowModal({
      ...blockData,
      lineId: blockData.resourceId,
    });
  };

  const openDowntimeShowModal = (blockData) => {
    onDowntimeShowModal(blockData);
  };
  const { open: handleOpenNotesPopUp } = useNotesPopUp();
  const handleViewNotesPopUp = ({ eventRecord }) => {
    const {
      data: { block },
    } = eventRecord;

    handleOpenNotesPopUp({ workBlockId: block.id, workOrderId: block.workOrder.id });
  };

  const unscheduleWorkOrder = ({ eventRecord }) => {
    const {
      block: { workOrder },
    } = eventRecord.data;

    const message = `This action will unschedule all of the work order’s (${workOrder.code}) scheduled blocks. However, it will not delete its published jobs, if any.`;

    confirmAction(
      message,
      "Unschedule",
      () => handleUnscheduleWorkOrder({ workOrderId: workOrder.id }),
      "Unschedule work order",
    );
  };

  const unscheduleBlock = ({ eventRecord }) => {
    const {
      block: { jobId },
    } = eventRecord.data;

    if (!jobId) {
      handleUnscheduleBlock(eventRecord.data);

      return;
    }

    const jobLink = packmanagerJobLinkRenderer({ applicationContext, jobId, prefix: "Job " });

    const message = `This action will unschedule the published block, but will not delete its corresponding job. To delete its job, go to ${jobLink}`;

    confirmAction(message, "Unschedule", () => handleUnscheduleBlock(eventRecord.data), "Unschedule work block");
  };

  const handleFullyScheduleWorkOrder = ({ eventRecord: { data: blockData } }) => {
    const { block: workBlock } = blockData;

    fullyScheduleWorkOrder(workBlock);
  };
  const createMenu = ({ items, blockData }) => {
    const { block } = blockData;

    if (!canUpdate && Block.isDowntime(block)) return downtimeReadOnlyMenu({ blockData, openDowntimeShowModal });

    if (!canUpdate && Block.isWorkBlock(block)) return workBlockReadOnlyMenu({ blockData, openWorkBlockShowModal });

    if (Block.isDowntime(block)) return downtimeMenu({ items, unscheduleBlock });

    const canPublishWorkBlock = policy.canPublishWorkBlock(Authorization.EntityTypes.PublishedWorkBlockType);

    if (Block.isWorkBlock(block))
      return workBlockContextMenu({
        blockData,
        canPublishWorkBlock,
        items,
        onFullyScheduleWorkOrder: handleFullyScheduleWorkOrder,
        onOpenEditWorkBlock,
        onPublishWorkBlock: handlePublishWorkBlock,
        onScheduleTeardownTime,
        onSplitWorkBlockAcrossShifts: handleSplitWorkBlockAcrossShifts,
        onUnscheduleTeardownTime: handleUnscheduleTeardownTime,
        onUpdateWorkOrder: handleUpdateWorkOrder,
        onViewNotesPopUp: handleViewNotesPopUp,
        openWorkOrderDetails,
        teardownTimes,
        unscheduleBlock,
        unscheduleWorkOrder,
      });

    return {
      deleteEvent: false,
      editEvent: false,
    };
  };

  return {
    eventFeatures: (gapFiller) => ({
      eventCopyPasteFeature: false,
      eventDragCreateFeature: {
        lockLayout: true,
        tooltipTemplate: function getDragCreateTipTemplate(props) {
          const {
            startDate,
            endDate,
            record: {
              data: { resourceId },
            },
          } = props;

          const line = lines.filter((lineToCheck) => lineToCheck.id === resourceId)[0];

          const overlapping = new OverlappingPlannedBreaks({ line });

          return renderToString(
            NewBlockTooltip({
              endDate,
              overlappingPlannedBreaks: overlapping.getCombinedOverlappingPlannedBreaks(
                new Timespan(startDate, endDate),
              ),
              startDate,
            }),
          );
        },
      },
      eventDragFeature: {
        constrainDragToTimeline: false,
        copyKey: "",
        tooltipTemplate: function getDragTipTemplate({ eventRecord, startDate, endDate, dragData }) {
          const resourceData = dragData.newResource?.data;
          if (isMultiScheduling(dragData) || !eventRecord) {
            return null;
          }

          return blockTooltipRenderer({
            applicationContext,
            block: eventRecord.data?.block,
            displaySnappedRemainingDuration: false,
            endDate,
            gapFiller,
            line: resourceData,
            showStatus: true,
            startDate,
          });
        },
      },
      eventMenuFeature: {
        processItems: ({ eventRecord, items }) => {
          const blockData = eventRecord.data;

          assign(items, createMenu({ blockData, items }));
        },
      },
      eventRenderer: blockRenderer({ applicationContext, openWorkOrderDetails, policy }),
      eventResizeFeature: {
        allowResizeToZero: true,
        tooltipTemplate: function getResizeTooltip({ record, startDate, endDate }) {
          if (!record) return null;

          let effectiveStartDate = startDate;
          let effectiveEndDate = endDate;

          if (record.data.startDate === startDate) {
            effectiveEndDate = endDate <= startDate ? DateTime.addDuration(startDate, 5, "minutes") : endDate;
          }

          if (record.data.endDate === endDate) {
            effectiveStartDate = endDate <= startDate ? DateTime.subtractDuration(endDate, 5, "minutes") : startDate;
          }

          const blockData = record.data;

          return blockTooltipRenderer({
            applicationContext,
            block: blockData.block,
            displaySnappedRemainingDuration: true,
            endDate: effectiveEndDate,
            gapFiller,
            line: blockData.block.line,
            showStatus: true,
            startDate: effectiveStartDate,
          });
        },
      },
      eventTooltipFeature: {
        template: ({ eventRecord, startDate, endDate }) =>
          blockTooltipRenderer({
            applicationContext,
            block: eventRecord.data.block,
            endDate,
            gapFiller,
            line: eventRecord.data.block.line,
            showStatus: true,
            startDate,
          }),
      },
    }),
  };
}

function isMultiScheduling(dragData) {
  return dragData.draggedEntities.length > 1;
}
